/** Configuração do cadastro: atributos próprios e regras tributárias da empresa. */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, X } from 'lucide-react';
import type { AttributeDef, AttributeType, TaxRule } from '@berytus/contracts';
import { api, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { can } from '@berytus/contracts';

const TYPE: Record<AttributeType, string> = { boolean: 'Sim / não', enum: 'Lista de opções', text: 'Texto', number: 'Número', date: 'Data' };

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '8vh', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', maxHeight: '84vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>{title}</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^(\d)/, 'a_$1').slice(0, 40);

export function AttributesPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'settings', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['attributes'], queryFn: () => api<AttributeDef[]>('/catalog/attributes') });
  const [editing, setEditing] = useState<AttributeDef | 'new' | null>(null);
  const del = useMutation({
    mutationFn: (d: AttributeDef) => api(`/catalog/attributes/${d.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attributes'] }),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Atributos personalizados</h1>
          <p>Informações próprias da sua operação (Linha, Coleção, Volume…). MIP, Controlado e Psicotrópico já vêm em toda conta (padrão Berytus): eles decidem a trava de medicamentos nos canais. Preencha no cadastro do produto, na planilha ou pela API, usando a chave como nome da coluna, e filtre o catálogo por elas.</p>
        </div>
        {canEdit && <button className="btn primary" type="button" onClick={() => setEditing('new')}><Plus size={16} /> Novo atributo</button>}
      </div>
      <div className="panel flush">
        {q.isPending ? <Loading /> : q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.data.length === 0 ? (
          <div className="empty"><b>Nenhum atributo</b>Crie o primeiro: por exemplo "MIP" do tipo sim/não.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead><tr><th>Nome</th><th>Chave</th><th>Tipo</th><th>Nível</th><th>Filtro</th><th>Canais</th><th className="num">Produtos</th><th /></tr></thead>
              <tbody>
                {q.data.map((d) => (
                  <tr key={d.id}>
                    <td><b>{d.label}</b>{d.system && <> <Tag tone="info">padrão Berytus</Tag></>}{d.unit && <span className="muted"> ({d.unit})</span>}{d.dataType === 'enum' && <div className="small muted">{d.options.join(', ')}</div>}</td>
                    <td className="code">{d.key}</td>
                    <td>{TYPE[d.dataType]}</td>
                    <td>{d.level === 'sku' ? 'SKU' : 'Produto'}</td>
                    <td>{d.filterable ? <Tag tone="ok">sim</Tag> : <Tag tone="off">não</Tag>}</td>
                    <td className="small">{d.sentToChannels ? Object.entries(d.channelMap).map(([c, a]) => `${c === 'mercadolivre' ? 'ML' : c}: ${a}`).join(', ') || 'sim' : '—'}</td>
                    <td className="num">{d.used ? <Link to={`/catalogo?attr=${d.key}`}>{int(d.used)}</Link> : 0}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {canEdit && <>
                        <button className="btn sm" type="button" onClick={() => setEditing(d)}>Editar</button>{' '}
                        {!d.system && <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} disabled={del.isPending}
                          onClick={() => confirm(`Apagar "${d.label}"? ${d.used ? `Os valores em ${d.used} produto(s) também serão apagados.` : ''}`) && del.mutate(d)}>Apagar</button>}
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && <AttrDialog d={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function AttrDialog({ d, onClose }: { d: AttributeDef | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    label: d?.label ?? '', key: d?.key ?? '', keyTouched: !!d, dataType: d?.dataType ?? ('boolean' as AttributeType), options: (d?.options ?? []).join('\n'),
    level: d?.level ?? 'product', unit: d?.unit ?? '', filterable: d?.filterable ?? true, sentToChannels: d?.sentToChannels ?? false,
    meli: d?.channelMap.mercadolivre ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...(d ? {} : { key: f.key }), label: f.label.trim(), dataType: f.dataType,
        options: f.dataType === 'enum' ? f.options.split(/\n|;/).map((o) => o.trim()).filter(Boolean) : [],
        level: f.level, unit: f.unit.trim() || null, filterable: f.filterable, sentToChannels: f.sentToChannels,
        channelMap: f.sentToChannels && f.meli.trim() ? { mercadolivre: f.meli.trim().toUpperCase() } : {}, position: d?.position ?? 0,
      };
      return d ? api(`/catalog/attributes/${d.id}`, { method: 'PUT', json: body }) : api('/catalog/attributes', { method: 'POST', json: body });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attributes'] }); qc.invalidateQueries({ queryKey: ['product-content'] }); onClose(); },
  });
  return (
    <Modal title={d ? `Editar ${d.label}` : 'Novo atributo'} onClose={onClose}>
      <div className="form-grid">
        <div className="field"><label htmlFor="at-label">Nome</label>
          <input className="input" id="at-label" autoFocus value={f.label} onChange={(e) => setF({ ...f, label: e.target.value, key: f.keyTouched ? f.key : slug(e.target.value) })} /></div>
        <div className="field"><label htmlFor="at-key">Chave (planilha e API)</label>
          <input className="input code" id="at-key" disabled={!!d} value={f.key} onChange={(e) => setF({ ...f, key: e.target.value, keyTouched: true })} />
          <span className="small muted">{d ? 'A chave não muda depois de criada.' : 'Minúsculas, números e _. Não muda depois.'}</span></div>
        <div className="field"><label htmlFor="at-type">Tipo</label>
          <select className="select" id="at-type" disabled={!!d?.system} value={f.dataType} onChange={(e) => setF({ ...f, dataType: e.target.value as AttributeType })}>
            {Object.entries(TYPE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
        <div className="field"><label htmlFor="at-level">Vale para</label>
          <select className="select" id="at-level" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value as 'product' | 'sku' })}>
            <option value="product">Produto (todas as variações)</option><option value="sku">Cada SKU (variação)</option>
          </select></div>
        {f.dataType === 'number' && <div className="field"><label htmlFor="at-unit">Unidade</label><input className="input" id="at-unit" placeholder="ml, g, W…" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></div>}
        {f.dataType === 'enum' && (
          <div className="field" style={{ gridColumn: '1 / -1' }}><label htmlFor="at-opts">Opções (uma por linha)</label>
            <textarea className="input" id="at-opts" rows={4} style={{ height: 'auto', padding: 10 }} value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} /></div>
        )}
      </div>
      <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" className="check" checked={f.filterable} onChange={(e) => setF({ ...f, filterable: e.target.checked })} /> Usar como filtro no catálogo
      </label>
      <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" className="check" checked={f.sentToChannels} onChange={(e) => setF({ ...f, sentToChannels: e.target.checked })} /> Enviar aos marketplaces
      </label>
      {f.sentToChannels && (
        <div className="field"><label htmlFor="at-meli">Atributo correspondente no Mercado Livre</label>
          <input className="input code" id="at-meli" placeholder="ex.: VOLUME_CAPACITY" value={f.meli} onChange={(e) => setF({ ...f, meli: e.target.value })} /></div>
      )}
      {save.error && <ErrorBox error={save.error} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" disabled={!f.label.trim() || !f.key || save.isPending} onClick={() => save.mutate()}>Salvar</button>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
}

export function TaxRulesPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'settings', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tax-rules'], queryFn: () => api<TaxRule[]>('/catalog/tax-rules') });
  const [editing, setEditing] = useState<TaxRule | 'new' | null>(null);
  const del = useMutation({
    mutationFn: (r: TaxRule) => api(`/catalog/tax-rules/${r.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tax-rules'] }),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Regras tributárias</h1>
          <p>Cada regra tem o ID dela em cada conta de canal (no Mercado Livre, o Tax Rule ID). O produto aponta para a regra; trocar o ID aqui vale para todos os produtos dela.</p>
        </div>
        {canEdit && <button className="btn primary" type="button" onClick={() => setEditing('new')}><Plus size={16} /> Nova regra</button>}
      </div>
      <div className="panel flush">
        {q.isPending ? <Loading /> : q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.data.length === 0 ? (
          <div className="empty"><b>Nenhuma regra</b>A importação da Qualidoc cria as regras sozinha a partir do tax_rule_id.</div>
        ) : (
          <table className="simple">
            <thead><tr><th>Nome</th><th>Mercado Livre (Tax Rule ID)</th><th className="num">Produtos</th><th /></tr></thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.name}</b>{r.description && <div className="small muted">{r.description}</div>}</td>
                  <td className="code">{r.channelIds.mercadolivre ?? '—'}</td>
                  <td className="num">{int(r.used)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canEdit && <>
                      <button className="btn sm" type="button" onClick={() => setEditing(r)}>Editar</button>{' '}
                      <button className="btn sm quiet" type="button" style={{ color: 'var(--err)' }} onClick={() => confirm(`Apagar "${r.name}"? ${r.used ? `${r.used} produto(s) ficam sem regra.` : ''}`) && del.mutate(r)}>Apagar</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <RuleDialog r={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function RuleDialog({ r, onClose }: { r: TaxRule | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: r?.name ?? '', description: r?.description ?? '', meli: String(r?.channelIds.mercadolivre ?? '') });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name.trim(), description: f.description.trim() || null, channelIds: f.meli.trim() ? { mercadolivre: /^\d+$/.test(f.meli.trim()) ? Number(f.meli.trim()) : f.meli.trim() } : {} };
      return r ? api(`/catalog/tax-rules/${r.id}`, { method: 'PUT', json: body }) : api('/catalog/tax-rules', { method: 'POST', json: body });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tax-rules'] }); qc.invalidateQueries({ queryKey: ['product-content'] }); onClose(); },
  });
  return (
    <Modal title={r ? `Editar ${r.name}` : 'Nova regra tributária'} onClose={onClose}>
      <div className="field"><label htmlFor="tr-name">Nome</label><input className="input" id="tr-name" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="ex.: Medicamentos ST" /></div>
      <div className="field"><label htmlFor="tr-desc">Descrição</label><input className="input" id="tr-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="field"><label htmlFor="tr-meli">Tax Rule ID no Mercado Livre</label><input className="input code" id="tr-meli" inputMode="numeric" value={f.meli} onChange={(e) => setF({ ...f, meli: e.target.value })} /></div>
      {save.error && <ErrorBox error={save.error} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate()}>Salvar</button>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
}

